import React, { FC, useState, ReactElement } from 'react'
import { Trans, t } from '@lingui/macro'
import { campaignFilterValidation } from 'shared/campaignValidations'
import { EditCampaignAttributeFilter } from '../EditCampaignAttributeFilter'

import Field from '../../../../../../components/common/form/field/Field'

import Loader from '../../../../../../components/common/loader/Loader'

import ResettableInput from '../../../../../../components/common/form/input/ResettableInput'
import classNames from 'classnames'
import { Flex, ButtonPrimary, ButtonClean, Box } from 'front-end/src/components'

import { IGroup } from 'front-end/types/Group'

import Input from '../../../../../../components/common/form/input/Input'
import './EditCampaignFilterModal.less'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'
import { EditCampaignEnrolmentFilterModalBody } from './EditCampaignEnrolmentFilterModalBody'
import { css } from '@emotion/react'
import {
  Modal,
  modalWidth
} from '../../../../../../components/common/modal/Modal'
import { upperFirst } from 'lodash'
import { EnrolmentTargetTypeEnum } from 'shared/enums/campaigns'

import {
  CampaignFilterInput,
  CampaignFilterTypeEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import { useCachedWorkspacesQuery } from 'front-end/src/app/stores/useCachedWorkspacesQuery'
import { useGroupsWithUserCountsQueryCached } from '../../filter-humanizers/GroupFilterHumanizer'
import { stripHTMLTags } from 'shared/HTML'
import { EditCampaignFilterContext } from 'front-end/src/pages/manage-campaign-page/components/campaign-audience/modals/enrolment-modal/EditCampaignFilterContext'
import { EditCampaignEventModal } from 'front-end/src/pages/manage-campaign-page/components/campaign-audience/modals/event-modal/EditCampaignEventModal'
import IconQuestion from 'front-end/src/components/common/icon/IconQuestion'

interface Props {
  editedFilter: CampaignFilterInput
  reject: () => {}
  resolve: (res: CampaignFilterInput) => {}
}

export const EditCampaignFilterModal: FC<Props> = (props) => {
  const { theme } = useLooopTheme()
  const { resolve, reject } = props
  const [editedFilter, setEditedFilter] = useState(props.editedFilter)
  const { data } = useCachedWorkspacesQuery()
  let workspaces = data?.workspaces

  let modalBody: ReactElement | null = null

  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState(editedFilter.ref_ids ?? [])

  const [comparator, setComparator] = useState(editedFilter.comparator)
  const { data: groupsQueryResult, loading } =
    useGroupsWithUserCountsQueryCached()

  if (loading) {
    return <Loader />
  }
  let subHeader
  let header
  let size: modalWidth = 'WIDE'
  if (editedFilter) {
    if (search.length > 0) {
      workspaces = workspaces?.filter((ws) => {
        const regex = new RegExp(search, 'i')
        return (
          ws.name.search(regex) !== -1 ||
          (ws.description && ws.description.search(regex) !== -1)
        )
      })
    }

    if (editedFilter.type === CampaignFilterTypeEnum.workspace) {
      header = t`Workspace`
      subHeader = t`Select workspaces`

      modalBody = (
        <>
          <Field stretch>
            <ResettableInput
              title={header}
              placeholder={t`Workspace name...`}
              value={search}
              onChange={(v) => {
                setSearch(v)
              }}
              showResetButton={search.length > 0}
              showSearchIcon={true}
              onReset={() => {
                setSearch('')
              }}
            />
          </Field>

          <div className="List">
            {selectedIds &&
              workspaces?.map((workspace) => {
                const isSelected = selectedIds.includes(workspace.id)
                const handleSelection = () => {
                  if (!isSelected) {
                    setSelectedIds([...selectedIds, workspace.id])
                  } else {
                    setSelectedIds(
                      selectedIds.filter((ws) => ws !== workspace.id)
                    )
                  }
                }
                return (
                  <Box
                    key={workspace.id}
                    className={classNames('List__Row', {
                      active: isSelected
                    })}
                    onClick={(ev: React.MouseEvent<HTMLDivElement>) => {
                      const target = ev.target as HTMLInputElement
                      if (target.type !== 'checkbox') {
                        handleSelection()
                      }
                    }}
                  >
                    <Flex alignItems={'center'}>
                      <input
                        // TODO: quick fix - rewrite this with <Checkbox/>
                        css={
                          selectedIds.includes(workspace.id) &&
                          css`
                            background-color: ${theme.colors.buttonPrimary
                              .default};
                            border-color: ${theme.colors.buttonPrimary.default};
                          `
                        }
                        title={t`Selected`}
                        className="Checkbox"
                        type="checkbox"
                        checked={selectedIds.includes(workspace.id)}
                        onChange={() => {
                          handleSelection()
                        }}
                      />
                      <div className="List__Row__firstCol">
                        {workspace.name}
                      </div>
                    </Flex>
                    <Box ml="12px" className="List__Row__secondCol">
                      {stripHTMLTags(workspace.description).replace(
                        /\s+/g,
                        ' '
                      )}
                    </Box>
                  </Box>
                )
              })}
          </div>
        </>
      )
    } else if (editedFilter.type === CampaignFilterTypeEnum.group) {
      header = t`Group`
      subHeader = t`Select groups`
      modalBody = (
        <>
          <Field stretch>
            <ResettableInput
              autoFocus
              title={header}
              placeholder={t`Group name...`}
              value={search}
              onChange={(v) => {
                setSearch(v)
              }}
              showResetButton={search.length > 0}
              showSearchIcon={true}
              onReset={() => {
                setSearch('')
              }}
            />
          </Field>

          <div className="List">
            {groupsQueryResult?.groups
              .filter((row) => {
                return row.name.toLowerCase().includes(search.toLowerCase()) // simple FE filtering
              })
              .map((group: IGroup) => {
                const isSelected = selectedIds.includes(group.id)
                const handleSelection = () => {
                  if (!isSelected) {
                    setSelectedIds([...selectedIds, group.id])
                  } else {
                    setSelectedIds(selectedIds.filter((ws) => ws !== group.id))
                  }
                }
                return (
                  <div
                    key={group.id}
                    className={classNames('List__Row', {
                      active: isSelected
                    })}
                    onClick={(ev: React.MouseEvent<HTMLDivElement>) => {
                      const target = ev.target as HTMLInputElement
                      if (target.type !== 'checkbox') {
                        handleSelection()
                      }
                    }}
                  >
                    <Flex alignItems={'center'}>
                      <input
                        // TODO: quick fix - rewrite this with <Checkbox/>
                        css={
                          selectedIds.includes(group.id) &&
                          css`
                            background-color: ${theme.colors.buttonPrimary
                              .default};
                            border-color: ${theme.colors.buttonPrimary.default};
                          `
                        }
                        title={t`Selected`}
                        className="Checkbox"
                        type="checkbox"
                        checked={selectedIds.includes(group.id)}
                        onChange={() => {
                          handleSelection()
                        }}
                      />
                      <div className="List__Row__firstCol">{group.name}</div>
                    </Flex>
                    <div className="List__Row__secondCol">
                      {group.usersCount}
                    </div>
                  </div>
                )
              })}
          </div>
        </>
      )
    } else if (editedFilter.type === CampaignFilterTypeEnum.value_comparison) {
      header = t`Attribute`
      subHeader = t`Filter to include or exclude only users that match the below
            criteria`

      modalBody = (
        <EditCampaignAttributeFilter
          setCampaignFilterComparator={setComparator}
          campaignFilterComparator={comparator}
        />
      )
    } else if (
      editedFilter.type === CampaignFilterTypeEnum.duration_comparison
    ) {
      header = t`Last active`
      subHeader = t`user is not active for`
      size = 'MEDIUM'
      modalBody = (
        <>
          <Input
            title={header}
            type="number"
            className="not-active-for-input"
            onChange={(val) => {
              setComparator({ ...comparator, value: { days: Number(val) } })
            }}
            value={(comparator && comparator.value.days) ?? ''}
          />{' '}
          <Trans>days</Trans>
        </>
      )
    } else if (editedFilter.type === CampaignFilterTypeEnum.event_invitation) {
      header = t`Events`
      subHeader = t`Filter to include only users that match the below criteria`
      size = 'MEDIUM'
      modalBody = <EditCampaignEventModal />
    } else {
      const TARGET_ENTITY_TRANSLATIONS = {
        [EnrolmentTargetTypeEnum.topic]: t`resource`,
        [EnrolmentTargetTypeEnum.pathway]: t`pathway`,
        [EnrolmentTargetTypeEnum.certification]: t`certification`
      }
      const targetEntityType: EnrolmentTargetTypeEnum = editedFilter.type.split(
        '_'
      )[0] as EnrolmentTargetTypeEnum
      const translatedEntity = TARGET_ENTITY_TRANSLATIONS[targetEntityType]

      if (!Object.keys(EnrolmentTargetTypeEnum).includes(targetEntityType)) {
        throw new Error(t`Unknown target entity type`)
      }
      header = t`${upperFirst(translatedEntity)}`
      subHeader = t`Filter to include only users that match the below criteria`

      modalBody = (
        <EditCampaignEnrolmentFilterModalBody
          setSelectedIds={setSelectedIds}
          selectedIds={selectedIds}
          targetEntityType={targetEntityType}
        />
      )
    }
  }
  const validation = campaignFilterValidation({
    ...editedFilter,
    ref_ids: selectedIds,
    // @ts-expect-error 9/11/2020
    comparator
  })

  return (
    <EditCampaignFilterContext.Provider
      value={{
        editedFilter,
        setEditedFilter
      }}
    >
      <Modal
        newDesign
        header={header}
        subHeader={subHeader}
        className={`EditCampaignFilterModal ${editedFilter.type}`}
        css={css`
          width: 644px;
          max-width: 644px !important;
          text-align: left;
          .Modal__header {
            padding-bottom: 16px;
          }
          .subHeader {
            margin-top: 8px !important;
          }
        `}
        size={size}
        controls={
          <Flex w="100%" justifyContent="flex-end">
            {[
              CampaignFilterTypeEnum.topic_enrolment,
              CampaignFilterTypeEnum.pathway_enrolment,
              CampaignFilterTypeEnum.certification_enrolment
            ].includes(editedFilter.type) && (
              <Box flexGrow={1} ml="-8px">
                <ButtonClean
                  leftIcon={<IconQuestion />}
                  onClick={() =>
                    window.open(
                      'https://support.looop.co/en/articles/6277722-campaign-enrolment-filters',
                      '_blank'
                    )
                  }
                >
                  <Trans>Help</Trans>
                </ButtonClean>
              </Box>
            )}
            {editedFilter.type === CampaignFilterTypeEnum.event_invitation && (
              <Box flexGrow={1} ml="-8px">
                <ButtonClean
                  leftIcon={<IconQuestion />}
                  onClick={() =>
                    window.open(
                      'https://support.looop.co/en/articles/6459395-event-filters-in-campaign',
                      '_blank'
                    )
                  }
                >
                  <Trans>Help</Trans>
                </ButtonClean>
              </Box>
            )}
            <ButtonClean onClick={reject}>
              <Trans>Cancel</Trans>
            </ButtonClean>
            <ButtonPrimary
              disabled={validation.result === false}
              onClick={() => {
                resolve({
                  ...editedFilter,
                  comparator,
                  ref_ids: selectedIds
                })
              }}
            >
              <Trans>Select</Trans>
            </ButtonPrimary>
          </Flex>
        }
      >
        {modalBody}
      </Modal>
    </EditCampaignFilterContext.Provider>
  )
}
